// askSmart-ui/src/pages/TOSP/components/BudgetSetupWizard.tsx
import React, { useEffect, useState } from "react";
import {
  BUDGET_IMPORT_ACCEPT,
  leavesOf,
  QboBudget,
  readQboBudget,
} from "../budgetImport";
import { resolveCategorySplits } from "../budgetAllocation";
import { formatCurrency } from "../format";
import {
  BudgetLine,
  CamCategory,
  CategorySplit,
  isSplitMappingComplete,
  newId,
} from "../types";

export interface BudgetSetup {
  lines: BudgetLine[];
  splits: CategorySplit[];
}

interface BudgetSetupWizardProps {
  isOpen: boolean;
  year: number;
  /** Existing setup when re-running the wizard on a year already built. */
  initial?: BudgetSetup;
  /** The configured categories, each carrying the split it's saved with. */
  camCategories: CamCategory[];
  onCancel: () => void;
  onFinish: (setup: BudgetSetup) => void;
}

type Step = 1 | 2;

const STEP_TITLES: Record<Step, string> = {
  1: "Upload the QBO export",
  2: "Assign a CAM category to every expense",
};

// Setting up a budget year: upload the export, then categorise its EXPENSE
// lines. Income is imported for the record but never categorised — revenue is
// TOSP's own Revenue Budget, and CAM categories exist to divide costs. On the
// sample export that is 43 lines to assign rather than 55.
//
// The splits aren't asked for — each category carries its own, defined once
// under Configuration — but they're copied onto the year here, so editing a
// category later can't re-divide a budget already built.
export default function BudgetSetupWizard({
  isOpen,
  year,
  initial,
  camCategories,
  onCancel,
  onFinish,
}: BudgetSetupWizardProps) {
  const [step, setStep] = useState<Step>(1);
  const [budget, setBudget] = useState<QboBudget | null>(null);
  const [fileName, setFileName] = useState("");
  const [lines, setLines] = useState<BudgetLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep(initial?.lines.length ? 2 : 1);
    setBudget(null);
    setFileName("");
    setLines(initial?.lines || []);
    setError(null);
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const parsed = await readQboBudget(file);
      if (parsed.rows.length === 0) {
        setError(
          "That doesn't look like a QuickBooks Budget Overview — no month columns found.",
        );
        setBudget(null);
        return;
      }
      setBudget(parsed);
      setFileName(file.name);
    } catch (err) {
      console.error("[TOSP] budget import failed:", err);
      setError("That file couldn't be read. Export it again as .xlsx or .csv.");
      setBudget(null);
    } finally {
      setReading(false);
    }
  };

  const incomeLeaves = budget ? leavesOf(budget, "income") : [];
  const expenseLeaves = budget ? leavesOf(budget, "expense") : [];

  // Categories that can't divide anything yet. Surfaced from the first screen
  // rather than at Finish — a split is fixed on the Configuration tab, so
  // finding out after categorising 43 lines means the work is already done.
  const unsplit = camCategories.filter((category) => !isSplitMappingComplete(category.split));
  const hasSplit = (name: string) => !unsplit.some((category) => category.name === name);

  const unsplitWarning = unsplit.length > 0 && (
    <div className="tosp-banner tosp-banner-warn">
      <span>
        No split configured for <strong>{unsplit.map((c) => c.name).join(", ")}</strong>. Set
        each one on the Configuration tab — lines assigned to them can't be allocated, and
        this budget can't be finished.
      </span>
    </div>
  );

  // Only leaves carry money. Parent accounts and section headings are kept too,
  // flagged as headers and in their original order, so the budget can be read
  // the way QuickBooks laid it out — but they're never categorised, allocated
  // or totalled, because their money is already in the rows beneath them.
  // "Total …" rows stay out entirely: they're derived, and the display can add
  // up its own.
  const buildLines = () => {
    if (!budget) return;
    const toLine = (
      row: (typeof expenseLeaves)[number],
      section: "income" | "expense",
      isHeader = false,
    ): BudgetLine => ({
      id: newId("bln"),
      description: row.code ? `${row.code} ${row.name}` : row.name,
      code: row.code,
      amount: isHeader ? null : row.total,
      months: isHeader ? undefined : row.months,
      section,
      ...(isHeader ? { isHeader: true } : {}),
      camCategory: "",
    });

    const expenseRows = budget.rows.filter(
      (row) =>
        row.section === "expense" && (row.kind === "leaf" || row.kind === "rollup" || row.kind === "section"),
    );

    setLines([
      ...incomeLeaves.map((row) => toLine(row, "income")),
      ...expenseRows.map((row) => toLine(row, "expense", row.kind !== "leaf")),
    ]);
    setError(null);
    setStep(2);
  };

  const assignCategory = (id: string, camCategory: string) =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, camCategory } : l)));

  // Income is never categorised, and headers carry no money, so neither can
  // hold the wizard up.
  const expenseLines = lines.filter((l) => l.section !== "income" && !l.isHeader);
  const importedIncome = lines.filter((l) => l.section === "income");
  const unassigned = expenseLines.filter((l) => !l.camCategory).length;

  const finish = () => {
    if (unassigned > 0) {
      setError(`${unassigned} line${unassigned === 1 ? "" : "s"} still need a category.`);
      return;
    }

    const splits = resolveCategorySplits(lines, initial?.splits || [], camCategories);
    // A category with no usable split can't divide anything, and it's fixed in
    // Configuration rather than here — so say where to go.
    const unmapped = splits
      .filter((split) => !isSplitMappingComplete(split))
      .map((split) => split.camCategory);
    if (unmapped.length > 0) {
      setError(
        `No split configured for ${unmapped.join(", ")} — set it on the Configuration tab first.`,
      );
      return;
    }

    onFinish({ lines, splits });
  };

  const total = expenseLines.reduce((sum, line) => sum + (line.amount ?? 0), 0);

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal tosp-modal-wide">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {year} Budget — Step {step} of 2: {STEP_TITLES[step]}
          </h2>
        </div>
        <div className="smrtdocs-modal-body">
          {/* ── Step 1: upload ── */}
          {step === 1 && (
            <>
              {unsplitWarning}

              <p className="tosp-wizard-text">
                Upload the completed QuickBooks Budget Overview for {year}. Its layout is
                read automatically — section headers, "Total" rows and parent accounts are
                left out, so only real budget lines come through.
              </p>

              <div className="tosp-field" style={{ marginBottom: 16 }}>
                <label className="tosp-label" htmlFor="tosp-budget-file">
                  QBO export
                </label>
                <input
                  id="tosp-budget-file"
                  type="file"
                  className="manage-input tosp-file-input"
                  accept={BUDGET_IMPORT_ACCEPT}
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                {reading && <p className="tosp-field-hint">Reading…</p>}
              </div>

              {budget && (
                <>
                  <p className="tosp-wizard-text">
                    <strong>{fileName}</strong>
                    {budget.year && budget.year !== year && (
                      <span className="tosp-field-error">
                        {" "}
                        — this export is for {budget.year}, not {year}.
                      </span>
                    )}
                  </p>
                  <div className="tosp-import-summary">
                    <div>
                      <strong>{expenseLeaves.length}</strong> expense lines to categorise
                      <div className="tosp-field-hint">
                        {formatCurrency(
                          expenseLeaves.reduce((sum, row) => sum + row.total, 0),
                        )}
                      </div>
                    </div>
                    <div>
                      <strong>{incomeLeaves.length}</strong> income lines, imported only
                      <div className="tosp-field-hint">
                        Revenue comes from the Revenue Budget, so these aren't categorised.
                      </div>
                    </div>
                    <div>
                      <strong>
                        {budget.rows.length - expenseLeaves.length - incomeLeaves.length}
                      </strong>{" "}
                      rows skipped
                      <div className="tosp-field-hint">
                        Sections, totals and parent accounts — already counted below them.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {error && <p className="tosp-field-error">{error}</p>}

              <div className="tosp-wizard-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="btn-save" onClick={buildLines} disabled={!budget}>
                  Continue
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: categorise ── */}
          {step === 2 && (
            <>
              {unsplitWarning}

              <p className="tosp-wizard-text">
                {expenseLines.length} expense line{expenseLines.length === 1 ? "" : "s"},{" "}
                {formatCurrency(total)} in total.{" "}
                {unassigned === 0 ? "All categorised." : `${unassigned} still to assign.`}
                {importedIncome.length > 0 && (
                  <>
                    {" "}
                    {importedIncome.length} income line
                    {importedIncome.length === 1 ? " was" : "s were"} imported without a
                    category — revenue is budgeted in TOSP, not divided by CAM.
                  </>
                )}
              </p>

              {camCategories.length === 0 && (
                <p className="tosp-field-error">
                  No CAM Categories configured — add them under Configuration first.
                </p>
              )}

              <div className="tosp-budget-lines">
                <div className="tosp-budget-line tosp-budget-line-header">
                  <div>Account</div>
                  <div>Amount</div>
                  <div>CAM Category</div>
                </div>
                {expenseLines.map((line) => (
                  <div key={line.id} className="tosp-budget-line">
                    <div className="tosp-cell-wrap">{line.description || "—"}</div>
                    <div>{line.amount === null ? "—" : formatCurrency(line.amount)}</div>
                    <select
                      className="manage-input"
                      aria-label={`CAM category for ${line.description}`}
                      value={line.camCategory}
                      onChange={(e) => assignCategory(line.id, e.target.value)}
                    >
                      <option value="">— Select —</option>
                      {camCategories.map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                          {hasSplit(category.name) ? "" : " — no split"}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {error && <p className="tosp-field-error">{error}</p>}

              <div className="tosp-wizard-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="btn-cancel" onClick={() => setStep(1)}>
                  Back
                </button>
                <button type="button" className="btn-save" onClick={finish}>
                  Finish
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
